import { Component, OnInit } from "@angular/core";
import { AppService } from "../app.service";
import { ActivatedRoute, Router } from "@angular/router";
import { HttpClient } from "@angular/common/http";

@Component({
    selector: "app-receipt",
    standalone: false,
    templateUrl: "./receipt.component.html",
    styleUrls: ["./receipt.component.css"],
})
export class ReceiptComponent implements OnInit {
    receiptList: any[] = [];
    apiKey: any;
    userKey: any;
    status: any;
    workspaceName: any;
    loading = true;
    txt: any;

    showErrorMsg: boolean = false;
    showErrorText: any;

    // Server-side filters
    startDate: string = "";
    endDate: string = "";
    purchaseType: string = ""; // "" = all, "grocery", "eating_out"

    // Server-side pagination
    currentPage: number = 1;
    itemsPerPage: number = 25; // maps to "limit"
    totalReceipts: number = 0; // server "total"
    totalPages: number = 0;
    jumpToPageNumber: number = 1;

    // Client-side (current page only) search + sort
    searchQuery: string = "";
    filteredReceipts: any[] = [];
    sortBy: string = "date-desc";

    // View mode
    viewMode: "grid" | "list" | "compact" = "grid";

    constructor(
        public appService: AppService,
        public router: Router,
        private http: HttpClient,
        private route: ActivatedRoute
    ) {
        this.userKey = this.route.snapshot.paramMap.get("key");
    }

    ngOnInit(): void {
        const savedViewMode = localStorage.getItem("receiptViewMode");
        if (savedViewMode) {
            this.viewMode = savedViewMode as "grid" | "list" | "compact";
        }

        const savedItemsPerPage = localStorage.getItem("receiptItemsPerPage");
        if (savedItemsPerPage) {
            this.itemsPerPage = parseInt(savedItemsPerPage, 10);
        }

        // Default date range: first day of current month → today
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        this.startDate = this.toDateString(first);
        this.endDate = this.toDateString(now);

        this.listReceipts();
    }

    private toDateString(d: Date): string {
        const y = d.getFullYear();
        const m = (d.getMonth() + 1).toString().padStart(2, "0");
        const day = d.getDate().toString().padStart(2, "0");
        return `${y}-${m}-${day}`;
    }

    async listReceipts() {
        this.loading = true;
        try {
            const wsJson = this.appService.get("xdbc.workspace");
            if (
                wsJson !== undefined &&
                typeof wsJson === "string" &&
                wsJson.trim() !== "" &&
                wsJson !== "undefined"
            ) {
                try {
                    const ws = JSON.parse(wsJson);
                    this.apiKey = ws.apiKey;
                    this.status = ws.status;
                    this.workspaceName = ws.workspaceName;
                } catch (err) {
                    console.error("Invalid JSON:", err);
                }
            }

            if (this.status === "ACTIVATED") {
                this.showErrorMsg = false;
            } else {
                this.showErrorMsg = true;
                this.showErrorText =
                    "The selected workspace is currently inactive. Please activate it to proceed.";
            }

            const offset = (this.currentPage - 1) * this.itemsPerPage;

            const payload: any = {
                userKey: this.userKey,
                startDate: this.startDate,
                endDate: this.endDate,
                limit: this.itemsPerPage,
                offset: offset,
                routeType: "UI_SESSION",
            };

            // Only include purchaseType when a specific one is selected
            if (this.purchaseType) {
                payload.purchaseType = this.purchaseType;
            }

            const signatureData = await this.appService.wssign(
                payload,
                this.workspaceName
            );

            if (!signatureData) {
                throw new Error("Failed to sign payload");
            }

            const signature = new TextDecoder().decode(signatureData);

            const response = await this.appService.wsPostRequestWithHeaders(
                payload,
                "receipt/list",
                this.apiKey,
                signature
            );

            this.loading = false;

            if (response && response.status == "Success") {
                // Response shape: data.data = receipts[], data.total, data.limit, data.offset
                this.receiptList = response?.data?.data || [];
                this.totalReceipts =
                    response?.data?.total ?? this.receiptList.length;
                this.totalPages = Math.ceil(this.totalReceipts / this.itemsPerPage);
                this.applyClientView();
                this.txt = response.message;
            } else {
                throw new Error(response?.message);
            }
        } catch (error: any) {
            this.loading = false;
            this.receiptList = [];
            this.filteredReceipts = [];
            this.txt =
                "Failed to load receipts: " + (error.message || "Unknown error");
        }
    }

    // ==================== Client view (search + sort on current page) ====================
    applyClientView() {
        let list = [...this.receiptList];

        if (this.searchQuery) {
            const q = this.searchQuery.toLowerCase();
            list = list.filter(
                (r) =>
                    r.merchantName?.toLowerCase().includes(q) ||
                    r.userNotes?.toLowerCase().includes(q) ||
                    r.purchaseType?.toLowerCase().includes(q) ||
                    (r.tags || []).some((t: string) => t.toLowerCase().includes(q))
            );
        }

        switch (this.sortBy) {
            case "date-desc":
                list.sort(
                    (a, b) =>
                        this.receiptTime(b) - this.receiptTime(a)
                );
                break;
            case "date-asc":
                list.sort(
                    (a, b) =>
                        this.receiptTime(a) - this.receiptTime(b)
                );
                break;
            case "total-desc":
                list.sort((a, b) => (b.total || 0) - (a.total || 0));
                break;
            case "total-asc":
                list.sort((a, b) => (a.total || 0) - (b.total || 0));
                break;
        }

        this.filteredReceipts = list;
    }

    private receiptTime(r: any): number {
        if (r.createdAt) return r.createdAt * 1000;
        const dt = `${r.receiptDate || ""}T${r.receiptTime || "00:00:00"}`;
        const t = new Date(dt).getTime();
        return isNaN(t) ? 0 : t;
    }

    onSearchChange() {
        this.applyClientView();
    }

    clearSearch() {
        this.searchQuery = "";
        this.applyClientView();
    }

    onSortChange() {
        this.applyClientView();
    }

    // ==================== Server filters ====================
    applyFilters() {
        this.currentPage = 1;
        this.listReceipts();
    }

    clearFilters() {
        const now = new Date();
        const first = new Date(now.getFullYear(), now.getMonth(), 1);
        this.startDate = this.toDateString(first);
        this.endDate = this.toDateString(now);
        this.purchaseType = "";
        this.searchQuery = "";
        this.currentPage = 1;
        this.listReceipts();
    }

    onItemsPerPageChange() {
        localStorage.setItem("receiptItemsPerPage", this.itemsPerPage.toString());
        this.currentPage = 1;
        this.listReceipts();
    }

    // ==================== Pagination (server-side) ====================
    nextPage() {
        if (this.currentPage < this.totalPages) {
            this.currentPage++;
            this.listReceipts();
            this.scrollToTop();
        }
    }

    previousPage() {
        if (this.currentPage > 1) {
            this.currentPage--;
            this.listReceipts();
            this.scrollToTop();
        }
    }

    goToFirstPage() {
        this.currentPage = 1;
        this.listReceipts();
        this.scrollToTop();
    }

    goToLastPage() {
        this.currentPage = this.totalPages;
        this.listReceipts();
        this.scrollToTop();
    }

    goToPage(page: number) {
        if (page >= 1 && page <= this.totalPages && page !== -1) {
            this.currentPage = page;
            this.listReceipts();
            this.scrollToTop();
        }
    }

    jumpToPage() {
        if (this.jumpToPageNumber >= 1 && this.jumpToPageNumber <= this.totalPages) {
            this.currentPage = this.jumpToPageNumber;
            this.listReceipts();
            this.scrollToTop();
        }
    }

    getPageNumbers(): number[] {
        const pages: number[] = [];
        const maxPagesToShow = 7;

        if (this.totalPages <= maxPagesToShow) {
            for (let i = 1; i <= this.totalPages; i++) pages.push(i);
        } else {
            if (this.currentPage <= 4) {
                for (let i = 1; i <= 5; i++) pages.push(i);
                pages.push(-1);
                pages.push(this.totalPages);
            } else if (this.currentPage >= this.totalPages - 3) {
                pages.push(1);
                pages.push(-1);
                for (let i = this.totalPages - 4; i <= this.totalPages; i++)
                    pages.push(i);
            } else {
                pages.push(1);
                pages.push(-1);
                for (let i = this.currentPage - 1; i <= this.currentPage + 1; i++)
                    pages.push(i);
                pages.push(-1);
                pages.push(this.totalPages);
            }
        }
        return pages;
    }

    getStartIndex(): number {
        return (this.currentPage - 1) * this.itemsPerPage;
    }

    getEndIndex(): number {
        return Math.min(this.currentPage * this.itemsPerPage, this.totalReceipts);
    }

    scrollToTop() {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }

    // ==================== View mode ====================
    setViewMode(mode: "grid" | "list" | "compact") {
        this.viewMode = mode;
        localStorage.setItem("receiptViewMode", mode);
    }

    // ==================== Helpers ====================
    pageSpend(): number {
        return this.filteredReceipts.reduce(
            (sum, r) => sum + (r.total || 0),
            0
        );
    }

    pageCurrency(): string {
        return this.filteredReceipts[0]?.currency || "";
    }

    formatMoney(value: any, currency: string = ""): string {
        if (value === null || value === undefined) return "—";
        const num = Number(value);
        if (isNaN(num)) return "—";
        return (currency ? currency + " " : "") + num.toFixed(2);
    }

    goBack() {
        history.back();
    }
}